import React from "react";
import Icon from "./Icon";

const TICKS = [
  "Client data never used to train AI models",
  "Encrypted in transit and at rest",
  "Data hosted in India [confirm region & provider]",
  "Named Grievance Officer for data requests",
];
const BADGES = ["DPDP Act, 2023", "DPDP Rules, 2025", "[ISO 27001 status]"];

const CONTROLS = [
  {
    n: "01",
    t: "Notice & consent",
    d: "Clear notice of what personal data is processed and why. Consent is recorded, and can be withdrawn as easily as it was given.",
  },
  {
    n: "02",
    t: "Purpose limitation & minimisation",
    d: "Data is used only to deliver your research, drafting and alerts — never sold, never used to train AI models.",
  },
  {
    n: "03",
    t: "Reasonable security safeguards",
    d: "Encryption, masking, role-based access control, SSO, monitoring for unauthorised access, and secure backups.",
  },
  {
    n: "04",
    t: "Breach intimation",
    d: "A tested incident-response process to inform the Data Protection Board and affected users without delay, with a detailed report within 72 hours.",
  },
  {
    n: "05",
    t: "Rights of Data Principals",
    d: "Requests to access, correct or erase personal data, and to raise a grievance, are handled through a named Grievance Officer.",
  },
  {
    n: "06",
    t: "Retention & erasure",
    d: "Personal data is erased once its purpose is served, and logs are retained for the period the Rules require — with a full audit trail.",
  },
];

const TIMELINE = [
  { d: "AUG 2023", t: "DPDP Act enacted" },
  {
    d: "13 NOV 2025",
    t: "DPDP Rules notified",
    s: "Data Protection Board constituted",
  },
  {
    d: "TODAY",
    t: "Vedum: controls in place",
    s: "Ready ahead of the deadline",
    now: true,
  },
  {
    d: "13 NOV 2026",
    t: "Consent managers",
    s: "Registration provisions apply",
  },
  {
    d: "13 MAY 2027",
    t: "Full obligations",
    s: "Notice, consent, safeguards, breach intimation, retention",
  },
];

const Security = () => (
  <section id="security" className="vd-section vd-section--soft">
    <div className="container">
      <div className="row g-5 mb-5">
        <div className="col-lg-7">
          <span className="vd-eyebrow">DATA PROTECTION · DPDP ACT, 2023</span>
          <h2 className="vd-h2">
            Your clients’ data, secured to the standard of India’s DPDP Act.
          </h2>
          <p className="vd-lead mb-3">
            Legal work runs on confidential information — notices, pleadings,
            financials and personal data of clients and their employees. Vedum
            secures that information in line with the Digital Personal Data
            Protection Act, 2023 and the DPDP Rules, 2025, so your practice
            meets its own obligations as a Data Fiduciary with confidence.
          </p>
          <p className="fw-semibold">
            Privilege is not negotiable. Neither is compliance.
          </p>
        </div>
        <div className="col-lg-5">
          <div className="vd-seal">
            <span className="vd-icon-box">
              <Icon name="shield" size={26} color="#F2A33A" />
            </span>
            <div>
              <strong>DPDP-compliant by design</strong>
              <br />
              <small>Built for Indian legal and tax data</small>
            </div>
          </div>
          <div className="vd-ticks">
            {TICKS.map((t) => (
              <span key={t}>
                <Icon name="check" size={16} color="#1E3270" /> {t}
              </span>
            ))}
          </div>
          <div className="vd-chips">
            {BADGES.map((b) => (
              <span key={b}>{b}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="h5 mb-1">How Vedum meets the DPDP requirements</h3>
        <p className="text-muted">
          Each obligation under the Act and Rules, and the control that
          addresses it
        </p>
      </div>
      <div className="row g-3 mb-5">
        {CONTROLS.map((c) => (
          <div className="col-md-6 col-lg-4" key={c.n}>
            <div className="vd-dpdp">
              <div className="vd-dpdp__t">
                <span>{c.n}</span>
                <span>{c.t}</span>
              </div>
              <p>{c.d}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="vd-card">
        <h3 className="h5 mb-1">The DPDP timeline — and where Vedum stands</h3>
        <p className="text-muted mb-4">
          Most obligations apply from 13 May 2027. Vedum’s controls are in place
          today.
        </p>
        <div className="vd-timeline">
          {TIMELINE.map((t) => (
            <div
              key={t.d}
              className={`vd-timeline__item ${t.now ? "is-now" : ""}`}
            >
              <b>{t.d}</b>
              <strong>{t.t}</strong>
              {t.s && <small>{t.s}</small>}
            </div>
          ))}
        </div>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4 pt-3 border-top">
          <span className="small">
            Need our data processing terms or a security questionnaire answered?
            Grievance Officer: <strong>[Name] · [privacy@vedum.ai]</strong>
          </span>
          <a href="#contact" className="fw-semibold">
            Request our DPDP pack →
          </a>
        </div>
      </div>
    </div>
  </section>
);

export default Security;
